import React, { useState } from 'react';
import { clsx } from 'clsx';

export interface AccordionItem {
  id: string;
  title: string;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface AccordionProps {
  items: AccordionItem[];
  defaultOpen?: string | string[];
  multiple?: boolean;
  variant?: 'default' | 'arrow' | 'plus';
  className?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  items,
  defaultOpen = [],
  multiple = false,
  variant = 'default',
  className,
}) => {
  const [openItems, setOpenItems] = useState<Set<string>>(
    new Set(Array.isArray(defaultOpen) ? defaultOpen : [defaultOpen])
  );

  const toggleItem = (itemId: string) => {
    const newOpenItems = new Set(openItems);

    if (newOpenItems.has(itemId)) {
      newOpenItems.delete(itemId);
    } else {
      if (!multiple) {
        newOpenItems.clear();
      }
      newOpenItems.add(itemId);
    }

    setOpenItems(newOpenItems);
  };

  const getIcon = (isOpen: boolean) => {
    if (variant === 'arrow') {
      return (
        <svg
          className={clsx('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    } else if (variant === 'plus') {
      return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          {isOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          )}
        </svg>
      );
    }
    return null;
  };

  return (
    <div className={clsx('space-y-2', className)}>
      {items.map((item) => {
        const isOpen = openItems.has(item.id);

        return (
          <div
            key={item.id}
            className={clsx('collapse collapse-arrow', isOpen && 'collapse-open', 'bg-base-200')}
          >
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={multiple ? undefined : 'accordion'}
              checked={isOpen}
              onChange={() => !item.disabled && toggleItem(item.id)}
              disabled={item.disabled}
              className="peer"
              aria-label={`Toggle ${item.title}`}
            />
            <div
              className={clsx(
                'collapse-title text-lg font-medium',
                item.disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex items-center justify-between">
                <span>{item.title}</span>
                {variant !== 'default' && getIcon(isOpen)}
              </div>
            </div>
            <div className="collapse-content">
              <div className="pt-2">{item.content}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export interface CollapseProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  variant?: 'default' | 'arrow' | 'plus';
  className?: string;
}

export const Collapse: React.FC<CollapseProps> = ({
  title,
  children,
  defaultOpen = false,
  variant = 'default',
  className,
}) => {
  return (
    <Accordion
      items={[
        {
          id: 'single',
          title,
          content: children,
        },
      ]}
      defaultOpen={defaultOpen ? 'single' : []}
      variant={variant}
      {...(className && { className })}
    />
  );
};
